import {useEffect} from 'react';
import {useNavigate} from 'react-router';
import {diApi} from '@di-shared/api/di';
import useApiError from '@di-shared/features/common/useApiError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {ROUTE_PATHS} from '@di-shared/routes';
import usePendingHandler from '@shared/usePendingHandler';

enum MileageDisconnectionType {
  MileageCancellation,
  IneligibleVehicleChange,
  UnsupportedVehicleChange,
  MileageConsentWithdrawal,
}

interface HomeMileageDisconnectionNoticeKrProps {
  vin: string;
}

function HomeMileageDisconnectionNoticeKr({vin}: HomeMileageDisconnectionNoticeKrProps) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const {openAlert, openConfirm} = useCcWebviewUtilsHelper();
  const {resolveError} = useApiError();

  const [checkNotification] = usePendingHandler(
    async (notificationId) => {
      await diApi.ecoMileageController.updateNotification({vin, notificationId, requestBody: {confirmed: true}});
    },
    async (error) => {
      await resolveError(error);
    }
  );

  useEffect(() => {
    (async function () {
      try {
        const notificationData = await diApi.ecoMileageController.getNotifications({vin});
        const integrationTerminationData = notificationData.data.find((item: any) => item.type === 'INTEGRATION_TERMINATION');

        const {data} = await diApi.ecoMileageController.getAgree({vin});

        const notificationId = integrationTerminationData?.id;
        const shouldShow = integrationTerminationData?.shouldShow;

        if (!shouldShow || (!notificationId && notificationId !== 0)) {
          return;
        }

        if (data?.estYn && data?.stopCd) {
          const mileageDisconnectionType = getMileageDisconnectionType({stopCd: data.stopCd, estYn: data.estYn});
          switch (mileageDisconnectionType) {
            case MileageDisconnectionType.MileageCancellation:
              await openMileageCancellationNoticeAlert();
              await checkNotification(notificationId);
              break;
            case MileageDisconnectionType.IneligibleVehicleChange:
              await openIneligibleVehicleChangeNoticeAlert();
              await checkNotification(notificationId);
              break;
            case MileageDisconnectionType.UnsupportedVehicleChange:
              await openUnsupportedVehicleChangeNoticeAlert();
              await checkNotification(notificationId);
              break;
            case MileageDisconnectionType.MileageConsentWithdrawal:
              const isConfirm = await openMileageConsentWithdrawalNoticeConfirm();
              await checkNotification(notificationId);
              if (isConfirm) {
                navigate(ROUTE_PATHS.MILEAGE_AGREEMENT.getFullPath(vin));
              }
              break;
            default:
          }
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  function getMileageDisconnectionType({stopCd, estYn}: {stopCd: string; estYn: number}) {
    if (stopCd === '01') {
      return MileageDisconnectionType.MileageCancellation;
    }
    if (stopCd === '03' && estYn === 1) {
      return MileageDisconnectionType.IneligibleVehicleChange;
    }
    if (stopCd === '03' && estYn === 0) {
      return MileageDisconnectionType.UnsupportedVehicleChange;
    }
    if (stopCd === '02') {
      return MileageDisconnectionType.MileageConsentWithdrawal;
    }
  }

  async function openMileageCancellationNoticeAlert() {
    return await openAlert({
      title: t('T30272'),
      message: t('T30273'),
    });
  }

  async function openIneligibleVehicleChangeNoticeAlert() {
    return await openAlert({
      title: t('T30272'),
      message: t('T30274'),
    });
  }

  async function openUnsupportedVehicleChangeNoticeAlert() {
    return await openAlert({
      title: t('T30272'),
      message: t('T30275'),
    });
  }

  async function openMileageConsentWithdrawalNoticeConfirm() {
    return await openConfirm({
      title: t('T30272'),
      message: t('T30276'),
      okText: t('T30277'),
      cancelText: t('T10017'),
    });
  }

  return <></>;
}

export default HomeMileageDisconnectionNoticeKr;
